/** Thin, typed HTTP client. The base URL is empty on the web (same origin) and absolute in native apps. */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: {
      error?: string;
      message?: string;
      problems?: string[];
      issues?: { path: string; message: string }[];
    } | null,
  ) {
    super(
      body?.message ??
        body?.problems?.join("; ") ??
        body?.issues?.map((i) => i.message).join("; ") ??
        `Request failed (${status})`,
    );
    this.name = "ApiError";
  }
}

export interface Api {
  request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<{ status: number; data: T; headers: Headers }>;
  get<T>(path: string): Promise<T>;
  send<T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T>;
}

export function createApi(options: {
  baseUrl: string;
  getToken?: () => string | null | Promise<string | null>;
  fetchFn?: typeof fetch;
}): Api {
  const doFetch = options.fetchFn ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    extra: Record<string, string> = {},
  ) {
    const headers: Record<string, string> = { ...extra };
    if (body !== undefined) headers["content-type"] = "application/json";
    const token = await options.getToken?.();
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await doFetch(`${options.baseUrl}${path}`, {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = res.status === 204 || res.status === 304 ? "" : await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok && res.status !== 304) throw new ApiError(res.status, data);
    return { status: res.status, data: data as T, headers: res.headers };
  }

  return {
    request,
    get: async <T>(path: string) => (await request<T>("GET", path)).data,
    send: async <T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown) =>
      (await request<T>(method, path, body)).data,
  };
}

/** Friendly message for any thrown error, for showing in the UI. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0 || err.status >= 500)
      return "The server is having trouble. Your changes are saved on this device.";
    return err.message;
  }
  if (err instanceof TypeError) return "You're offline. Your changes are saved on this device.";
  return err instanceof Error ? err.message : "Something went wrong.";
}
